import { useState, useEffect, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { mergeMap, of } from "rxjs";
import { Button, IconButton, SvgIcon } from "@mui/material";
import { jsPDF } from "jspdf";
import { autoTable } from "jspdf-autotable";
import * as XLSX from "xlsx";

import "./style.scss";
import { ReactComponent as CrossIcon } from "../../assets/crossIcon.svg";
import { ReactComponent as GraphIcon } from "../../assets/graph.icon.svg";
import { ReactComponent as DownloadIcon } from "../../assets/download.icon.svg";
import { ReactComponent as ColumnsIcon } from "../../assets/columns.icon.svg";
import { TivaDataWebSocketContainer } from "../../common/containers/tiva-data/tiva-data-web-socket.container";
import { SectionItem } from "../../common/interfaces/comps/menu-item.interface";
import { ExportFileType } from "../../common/enums/export-file-type";
import { isMobileDevice } from "../../common/utilities/platform/platform.util";
import { DynamicTable } from "../../components/table/table.comp";
import { PaginationBlock } from "../../components/pagination/pagination.comp";
import { LoaderView } from "../../components/loader/loader.comp";
import { MultipleCheckboxMenuFilter } from "../../components/menu-filter/menu-filter.comp";
import { ModalTemplate } from "../../components/modal-template/modal-template.comp";
import FileDownloadView from "../admin/file-download/file-download";

interface TivaDataFilter {
    id?: number;
    operator: string;
    members: any[];
}

interface ModelType {
    id: string;
    name: string;
}

const TivaDataModalView = ({ handleClose }: { handleClose: () => void }) => {
    const navigate = useNavigate();
    const { tivaApp, isAuth, rpc$ } = TivaDataWebSocketContainer.useContainer();
    const [searchParams] = useSearchParams();
    const [pageParam] = useState(searchParams.get("tivaDataPage"));
    const [perPageParam] = useState(searchParams.get("tivaDataPerPage"));
    const [columnsParam] = useState(searchParams.get("tivaDataColumns"));
    const [payloadParam] = useState(searchParams.get("tivaDataPayload"));
    const [data] = useState<any>(payloadParam ? JSON.parse(payloadParam) : undefined);
    const [globalFilters, setGlobalFilters] = useState<TivaDataFilter>();
    const [measureFilters] = useState<TivaDataFilter>(
        (data?.measureFilters as TivaDataFilter) ?? { operator: "and", members: [] }
    );
    const [dimensionFilters] = useState<TivaDataFilter>(
        (data?.dimensionFilters as TivaDataFilter) ?? { operator: "and", members: [] }
    );
    const [query] = useState<string>(data?.query ?? "");
    const [modelType] = useState<ModelType>(data?.modelType);
    const [viewsDictionaries, setViewsDictionaries] = useState<object>();
    const [measures, setMeasures] = useState<any[]>();
    const [dimensions, setDimensions] = useState<any[]>();
    const [selectedGroups] = useState<any[]>(data?.groups ?? []);
    const [selectedMeasures, setSelectedMeasures] = useState<string[]>(data?.measures ?? []);
    const [selectedDimensions, setSelectedDimensions] = useState<string[]>(
        data?.propertiesToShow ?? []
    );
    const [columns, setColumns] = useState<string[]>(
        columnsParam ? JSON.parse(columnsParam) : [...selectedDimensions, ...selectedMeasures]
    );
    const [filterList, setFilterList] = useState<SectionItem<string>[]>([]);
    const [list, setList] = useState<any[]>([]);
    const [annotation, setAnnotation] = useState<any>();
    const [totalCount, setTotalCount] = useState<number>();
    const [perPageList] = useState<number[]>([25, 50, 100]);
    const [perPage, setPerPage] = useState<number>(
        perPageParam && !isNaN(Number(perPageParam))
            ? Number(perPageParam)
            : perPageList.length
              ? perPageList[0]
              : 25
    );
    const [page, setPage] = useState<number>(
        pageParam && !isNaN(Number(pageParam)) ? Number(pageParam) : 1
    );
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [downloadOpen, setDownloadOpen] = useState<boolean>(false);

    const updateSearchParams = useCallback(() => {
        navigate(
            `${location.pathname}?tivaDataPayload=${JSON.stringify(data)}&tivaDataColumns=${JSON.stringify(columns)}&tivaDataPage=${page}&tivaDataPerPage=${perPage}`
        );
    }, [navigate, data, columns, page, perPage]);

    useEffect(() => {
        updateSearchParams();
    }, [page, updateSearchParams]);

    useEffect(() => {
        if (!isAuth || !tivaApp?.organizationId) return;

        rpc$("GET_GLOBAL_FILTERS", { organizationId: tivaApp.organizationId })
            .pipe(
                mergeMap(response =>
                    response && !!(response as Array<any>)?.length
                        ? of((response as Array<any>)[0])
                        : rpc$("ADD_GLOBAL_FILTER", {
                              organizationId: tivaApp.organizationId,
                              message: { filter: { operator: "and", members: [] } }
                          })
                )
            )
            .subscribe(result => {
                setGlobalFilters({
                    id: result.id,
                    operator: result.filter.operator,
                    members: result.filter.members
                });
            });
    }, [tivaApp?.organizationId, isAuth, rpc$]);

    useEffect(() => {
        if (!data || !isAuth || !tivaApp?.appId || !tivaApp?.organizationId) return;

        rpc$("GET_DEFAULT_VIEWS", {
            appId: tivaApp.appId,
            organizationId: tivaApp.organizationId
        }).subscribe((result: any) => {
            setViewsDictionaries({ ...result, ...(data.viewsDictionaries || {}) });
        });
    }, [data, rpc$, isAuth, tivaApp?.appId, tivaApp?.organizationId]);

    useEffect(() => {
        if (!isAuth || !tivaApp?.appId || !tivaApp?.organizationId || !viewsDictionaries) return;

        rpc$("GET_CUBE_MEASURES", {
            modelType: modelType?.id,
            appId: tivaApp.appId,
            organizationId: tivaApp.organizationId,
            viewsDictionaries
        }).subscribe(({ measures: result, error }: any) => {
            if (!error) {
                setMeasures(result || []);
            }
        });

        rpc$("GET_CUBE_DIMENSIONS", {
            modelType: modelType?.id,
            appId: tivaApp.appId,
            organizationId: tivaApp.organizationId,
            viewsDictionaries
        }).subscribe(({ dimensions: result, error }: any) => {
            if (!error) {
                setDimensions(result || []);
            }
        });
    }, [rpc$, isAuth, tivaApp, modelType, viewsDictionaries]);

    useEffect(() => {
        if (!dimensions || !measures) return;

        setFilterList([
            {
                title: "Columns",
                list: dimensions
                    .sort((a, b) => a.shortTitle.localeCompare(b.shortTitle))
                    .map(item => ({
                        label: item.shortTitle,
                        key: item.name,
                        value: item.name
                    }))
            },
            {
                title: "Measures",
                list: measures
                    .sort((a, b) => a.shortTitle.localeCompare(b.shortTitle))
                    .map(item => ({
                        label: item.shortTitle,
                        key: item.name,
                        value: item.name
                    }))
            }
        ]);
    }, [dimensions, measures, data]);

    useEffect(() => {
        if (!measures || !dimensions) return;

        const newDimensions = [...selectedDimensions];
        const newMeasures = [...selectedMeasures];
        selectedDimensions.forEach(item => {
            if (!columns.includes(item)) {
                const index = newDimensions.indexOf(item);
                if (index !== -1) {
                    newDimensions.splice(index, 1);
                }
            }
        });
        selectedMeasures.forEach(item => {
            if (!columns.includes(item)) {
                const index = newMeasures.indexOf(item);
                if (index !== -1) {
                    newMeasures.splice(index, 1);
                }
            }
        });
        columns.forEach(column => {
            if (!selectedDimensions.includes(column) && !selectedMeasures.includes(column)) {
                if (dimensions.find(item => item.name === column)) {
                    newDimensions.push(column);
                } else if (measures.find(item => item.name === column)) {
                    newMeasures.push(column);
                }
            }
        });

        if (newDimensions.length !== selectedDimensions.length) {
            setSelectedDimensions(newDimensions);
        }
        if (newMeasures.length !== selectedMeasures.length) {
            setSelectedMeasures(newMeasures);
        }

        updateSearchParams();
    }, [columns, selectedDimensions, selectedMeasures, measures, dimensions, updateSearchParams]);

    useEffect(() => {
        setIsLoading(true);

        if (!tivaApp || !isAuth || !globalFilters) return;

        const dimensionWithGlobalFilters = globalFilters?.members
            ? { operator: "and", members: [...globalFilters.members, ...dimensionFilters.members] }
            : dimensionFilters;

        const params = {
            appId: tivaApp.appId,
            organizationId: tivaApp.organizationId,
            modelType: modelType?.id,
            filters: [dimensionWithGlobalFilters, measureFilters],
            measures: selectedMeasures,
            groups: selectedGroups,
            propsToShow: selectedDimensions,
            limit: perPage,
            startFrom: (page - 1) * perPage,
            sortBy: []
        };
        rpc$("GET_CUBE_LIST", params).subscribe((result: any) => {
            setList(result.list);
            setAnnotation(result.annotation);
            setTotalCount(result.totalNumber);
            setIsLoading(false);
        });
    }, [
        tivaApp,
        isAuth,
        rpc$,
        globalFilters,
        measureFilters,
        dimensionFilters,
        selectedDimensions,
        selectedMeasures,
        selectedGroups,
        modelType,
        perPage,
        page
    ]);

    const updatePerPage = (value: number) => {
        setPage(1);
        setPerPage(value);
    };

    const handleCreateGrapClick = () => {
        console.info("--- create graph ---");
    };

    const handleDownloadOpen = () => {
        setDownloadOpen(true);
    };

    const handleDownloadClose = () => {
        setDownloadOpen(false);
    };

    const handleDownloadExport = (fileName?: string, type?: ExportFileType) => {
        setDownloadOpen(false);

        if (fileName && type) exportTable(fileName, type);
    };

    const exportTable = (fileName: string, type: ExportFileType) => {
        if (!tivaApp || !globalFilters) return;

        setIsLoading(true);

        const dimensionWithGlobalFilters = globalFilters?.members
            ? { operator: "and", members: [...globalFilters.members, ...dimensionFilters.members] }
            : dimensionFilters;

        const params = {
            appId: tivaApp.appId,
            organizationId: tivaApp.organizationId,
            modelType: modelType?.id,
            filters: [dimensionWithGlobalFilters, measureFilters],
            measures: selectedMeasures,
            groups: selectedGroups,
            propsToShow: selectedDimensions,
            limit: totalCount,
            startFrom: 0,
            sortBy: []
        };
        rpc$("GET_CUBE_LIST", params).subscribe((result: any) => {
            const exportColumns = columns.map(column => getTitle(column, result.annotation));
            const exportList = result.list
                .map((item: any) =>
                    columns.map(key => [getTitle(key, result.annotation), item[key]])
                )
                .map((item: any) => Object.fromEntries(item));

            switch (type) {
                case ExportFileType.pdf:
                    exportPDF(fileName, exportList, exportColumns);
                    break;
                case ExportFileType.csv:
                    exportCSV(fileName, exportList, exportColumns);
                    break;
                case ExportFileType.xls:
                    exportXLS(fileName, exportList);
                    break;
            }

            setIsLoading(false);
        });
    };

    const getTitle = (key: string, annotation: any) =>
        annotation?.dimensions[key]?.shortTitle ??
        annotation?.measures[key]?.shortTitle ??
        parseTitle(key);

    const parseTitle = (key: string) =>
        (key.split(".").pop() ?? key)
            .split(/[a-z](?=[A-Z])/g)
            .join(" ")
            .toLowerCase()
            .firstLetterToUppercase();

    const exportPDF = (fileName: string, list: any[], columns: string[]) => {
        const doc = new jsPDF();
        autoTable(doc, {
            head: [columns],
            body: list.map(item => Object.values(item)),
            headStyles: {
                fontSize: 8,
                fontStyle: "bold",
                textColor: [66, 89, 118],
                fillColor: [218, 225, 237]
            },
            styles: {
                fontSize: 8,
                fontStyle: "normal",
                textColor: [66, 89, 118],
                fillColor: [255, 255, 255]
            },
            alternateRowStyles: {
                fillColor: [246, 247, 249]
            }
        });
        doc.save(`${fileName}.pdf`);
    };

    const exportCSV = (fileName: string, list: any[], columns: string[]) => {
        const csvString = [columns, ...list.map(item => Object.values(item))]
            .map(row => row.join(","))
            .join("\n");

        const blob = new Blob([csvString], { type: "text/csv" });

        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = `${fileName}.csv`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
    };

    const exportXLS = (fileName: string, list: any[]) => {
        const worksheet = XLSX.utils.json_to_sheet(list);
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, "TIVA Data");

        XLSX.writeFile(workbook, `${fileName}.xlsx`);
    };

    return (
        <div className="tiva-data-content">
            <div className="tiva-data-header">
                <div className="tiva-data-header-actions">
                    <Button
                        className="tiva-data-header-actions__button primary-button--icon-first"
                        variant="text"
                        startIcon={
                            <SvgIcon
                                className="primary-button--icon-first__icon"
                                component={GraphIcon}
                                inheritViewBox
                            />
                        }
                        onClick={handleCreateGrapClick}
                    >
                        <span className="primary-button--icon-first__text">Create Graph</span>
                    </Button>
                    <Button
                        className="tiva-data-header-actions__button primary-button--icon-first"
                        variant="text"
                        startIcon={
                            <SvgIcon
                                className="primary-button--icon-first__icon"
                                component={DownloadIcon}
                                inheritViewBox
                            />
                        }
                        onClick={handleDownloadOpen}
                    >
                        <span className="primary-button--icon-first__text">Download as File</span>
                    </Button>
                </div>
                <IconButton className="tiva-data-header-close" onClick={handleClose}>
                    <CrossIcon />
                </IconButton>
            </div>
            <div className="tiva-data-body">
                <div className="tiva-data-title">
                    <span className="tiva-data-title__query">{query ?? ""}</span>
                    <div className="tiva-data-title__filter">
                        <MultipleCheckboxMenuFilter
                            label="Show Columns"
                            multipleList={filterList}
                            checkedList={columns}
                            setCheckedList={setColumns}
                            startIcon={ColumnsIcon}
                            showCounter={true}
                            rightPosition={true}
                        />
                    </div>
                </div>
                {!annotation || totalCount == undefined ? (
                    <LoaderView show={true}></LoaderView>
                ) : (
                    <div className="tiva-data-table-block">
                        <div className="tiva-data-table">
                            <DynamicTable
                                list={list}
                                columns={columns}
                                setColumns={setColumns}
                                annotation={annotation}
                            />
                            <LoaderView show={isLoading}></LoaderView>
                        </div>
                        <div className="tiva-data-pagination">
                            <PaginationBlock
                                totalCount={totalCount}
                                page={page}
                                setPage={setPage}
                                perPageList={perPageList}
                                perPage={perPage}
                                setPerPage={updatePerPage}
                                showFirstButton={!isMobileDevice()}
                                showLastButton={!isMobileDevice()}
                                siblingCount={isMobileDevice() ? 0 : undefined}
                                boundaryCount={isMobileDevice() ? 0 : undefined}
                            />
                        </div>
                    </div>
                )}
            </div>
            <ModalTemplate
                open={downloadOpen}
                width={654}
                height={404}
                handleClose={handleDownloadClose}
            >
                <FileDownloadView handleClose={handleDownloadExport} />
            </ModalTemplate>
        </div>
    );
};

export default TivaDataModalView;
